import { ChangeDetectionStrategy, Component, EventEmitter, Input, OnDestroy, OnInit, Output } from '@angular/core';
import { Router } from '@angular/router';

import { Observable, Subscription, combineLatest, map, take } from 'rxjs';
import { Store } from '@ngrx/store';

import { AppState, RootPaths } from '@fcom/core';
import { LanguageService } from '@fcom/ui-translate';
import { FinnairOrder } from '@fcom/dapi/api/models';
import { ButtonTheme, ModalButtons } from '@fcom/ui-components';
import { ElementActions, ElementTypes, GaContext } from '@fcom/common';
import { GtmService } from '@fcom/common/gtm';
import { unsubscribe } from '@fcom/core/utils';
import { VOLUNTARY_CHANGE_STEPS } from '@fcom/voluntary-change/voluntary-change-config';
import { getChangeableBoundIds } from '@fcom/common-booking';

import { ChangeActions } from '../../../../store/actions/change.actions';
import { ChangeFlightInfo } from '../../../../interfaces';

@Component({
  selector: 'fin-mmb-change-flight-general-info-modal',
  templateUrl: './change-flight-general-info-modal.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: false,
})
export class ManageBookingChangeFlightGeneralInfoModalComponent implements OnInit, OnDestroy {
  @Input() booking$: Observable<FinnairOrder>;
  @Input() isOpen = false;
  @Output() modalClosed = new EventEmitter<boolean>();
  subscriptions: Subscription = new Subscription();

  readonly ButtonTheme = ButtonTheme;
  readonly ModalButtons = ModalButtons;

  changeableBoundIds$: Observable<string[]>;
  changeFlightInfo$: Observable<ChangeFlightInfo>;

  constructor(
    private store$: Store<AppState>,
    private router: Router,
    private lang: LanguageService,
    public gtmService: GtmService
  ) {}

  ngOnInit(): void {
    this.subscriptions.add(this.modalClosed.subscribe(() => this.trackClose()));
    this.changeableBoundIds$ = this.store$.pipe(getChangeableBoundIds());

    this.changeFlightInfo$ = combineLatest([this.booking$, this.changeableBoundIds$]).pipe(
      map(([booking, changeableBoundIds]) => {
        if (changeableBoundIds.length === 1) {
          const selectedBoundIndex = booking.bounds.findIndex((bound) => bound.id === changeableBoundIds[0]);

          return {
            onlyOneChangeableBound: true,
            selectedBound: booking.bounds[selectedBoundIndex],
            isInboundFlight: selectedBoundIndex === booking.bounds.length - 1,
            nextStepPath: VOLUNTARY_CHANGE_STEPS.SELECT_DATES.path,
          };
        } else {
          return {
            onlyOneChangeableBound: false,
            nextStepPath: VOLUNTARY_CHANGE_STEPS.SELECT_BOUNDS.path,
          };
        }
      })
    );
  }

  ngOnDestroy(): void {
    unsubscribe(this.subscriptions);
  }

  closeModal(): void {
    this.modalClosed.next(true);
  }

  trackClose(): void {
    this.subscriptions.add(
      this.booking$.pipe(take(1)).subscribe((booking) => {
        this.gtmService.trackElement(
          'view-pnr_change_abort-generalinfo',
          GaContext.MANAGE_BOOKING,
          ElementTypes.MODAL,
          undefined,
          ElementActions.CLOSE,
          `recLoc-${booking?.otherInformation.analyticsToken}`
        );
      })
    );
  }

  goToNextStep(changeFlightInfo: ChangeFlightInfo): void {
    if (changeFlightInfo.onlyOneChangeableBound) {
      if (changeFlightInfo.isInboundFlight) {
        this.store$.dispatch(ChangeActions.setInbound({ bound: changeFlightInfo.selectedBound }));
      } else {
        this.store$.dispatch(ChangeActions.setOutbound({ bound: changeFlightInfo.selectedBound }));
      }
    }

    this.router.navigate([
      '/',
      this.lang.langValue || 'en',
      ...RootPaths.VOLUNTARY_CHANGE_ROOT.split('/'),
      changeFlightInfo.nextStepPath,
    ]);
  }
}
